import { memo, useRef, useLayoutEffect, useState, useEffect, useCallback } from 'react';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Spin, Skeleton } from 'antd';
import Row from 'antd/lib/row';
import { useDispatch, useSelector } from 'react-redux';
import { firstGetListMessage } from 'providers/MessengerProvider/slice';
import MessageCell from './MessageCell';
import { LoadingOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { Button } from 'antd';

// Skeleton for loading older messages
const MessageSkeleton = ({ isOwn = false }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: isOwn ? 'flex-end' : 'flex-start',
      padding: '8px 16px',
      gap: 8,
    }}
  >
    {!isOwn && <Skeleton.Avatar active size={32} />}
    <div style={{ maxWidth: '60%' }}>
      <Skeleton.Input active size="small" style={{ width: isOwn ? 150 : 200, marginBottom: 4 }} />
      <Skeleton.Input active size="small" style={{ width: isOwn ? 100 : 120 }} />
    </div>
    {isOwn && <Skeleton.Avatar active size={32} />}
  </div>
);

const MessageList = ({
  messageIds = [],
  messageEntities = {},
  getMessageList,
  hasMore,
  currentUserId,
  currentUserRole,
  onSendMessage,
}) => {
  const dispatch = useDispatch();
  const latestMessage = messageIds[0];
  const messageListRef = useRef();
  const firstGetList = useSelector((state) => state.messenger.firstGetList);
  const [optimisticMessages, setOptimisticMessages] = useState([]);
  const isSending = useSelector((state) => state.messenger.isSending);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [newMessagePreview, setNewMessagePreview] = useState(null);
  const prevLatestMessageRef = useRef(latestMessage);
  const isViewingOldMessagesRef = useRef(false);

  useLayoutEffect(() => {
    if (messageListRef.current) {
      dispatch(firstGetListMessage());

      const isNewMessage = prevLatestMessageRef.current && prevLatestMessageRef.current !== latestMessage;

      if (isNewMessage && isViewingOldMessagesRef.current) {
        // User is viewing old messages - show notification instead of scrolling
        const newMessage = messageEntities[latestMessage];
        if (newMessage) {
          const senderId = _get(newMessage, 'sender._id') || _get(newMessage, 'sender');
          const isOwnMessage = senderId === currentUserId;

          if (!isOwnMessage) {
            // Show preview for messages from others
            const senderName = _get(newMessage, 'sender.name') || _get(newMessage, 'sender.username') || 'Fonehouse';
            const messageContent = _get(newMessage, 'text') || _get(newMessage, 'content') || 'Tin nhắn mới';

            setNewMessagePreview({
              senderName,
              content: messageContent,
            });
          } else {
            // Own message - scroll to bottom
            messageListRef.current.scrollTo(0, 0);
            setNewMessagePreview(null);
          }
        }
      } else if (!isViewingOldMessagesRef.current) {
        // Not viewing old messages - auto scroll to bottom
        messageListRef.current.scrollTo(0, 0);
        setNewMessagePreview(null);
      }

      prevLatestMessageRef.current = latestMessage;
    }
  }, [dispatch, latestMessage, messageEntities, currentUserId]);

  // Attach wheel event listener to capture scroll immediately
  useEffect(() => {
    const container = messageListRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtTop = scrollTop === 0;
      const isAtBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight;
      const scrollingUp = e.deltaY < 0;
      const scrollingDown = e.deltaY > 0;

      // If we can scroll in the wheel direction, prevent parent from scrolling
      const canScrollUp = !isAtTop && scrollingUp;
      const canScrollDown = !isAtBottom && scrollingDown;

      if (canScrollUp || canScrollDown) {
        e.stopPropagation();
      }
    };

    // Use capture phase to intercept event before it bubbles
    container.addEventListener('wheel', handleWheel, { passive: true, capture: true });

    return () => {
      container.removeEventListener('wheel', handleWheel, { capture: true });
    };
  }, []);

  // Handle scroll to show/hide scroll down button
  const handleScroll = useCallback((e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtBottom = Math.abs(scrollTop) < 100;
    const hasScrollableContent = scrollHeight > clientHeight;
    const isViewingOld = hasScrollableContent && !isAtBottom;

    // Update ref synchronously for immediate access in useLayoutEffect
    isViewingOldMessagesRef.current = isViewingOld;
    setShowScrollDown(isViewingOld);

    // Clear preview when user scrolls back to bottom
    if (!isViewingOld) {
      setNewMessagePreview(null);
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTo(0, 0);
      isViewingOldMessagesRef.current = false;
      setNewMessagePreview(null);
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    // Focus the scroll container to ensure immediate scroll response
    if (messageListRef.current) {
      messageListRef.current.focus();
    }
  }, []);

  // Handle retry for failed messages
  const handleRetry = useCallback(
    (failedMessage) => {
      if (!failedMessage.originalMessage) return;

      // Mark as sending again
      setOptimisticMessages((prev) =>
        prev.map((msg) => (msg.id === failedMessage.id ? { ...msg, isOptimistic: true, status: undefined } : msg))
      );

      // Retry sending
      onSendMessage(failedMessage.originalMessage)
        .then(() => {
          setOptimisticMessages((prev) => prev.filter((msg) => msg.id !== failedMessage.id));
        })
        .catch(() => {
          setOptimisticMessages((prev) =>
            prev.map((msg) => (msg.id === failedMessage.id ? { ...msg, isOptimistic: false, status: 'failed' } : msg))
          );
        });
    },
    [onSendMessage]
  );

  if (!firstGetList) {
    return (
      <Row
        style={{
          padding: 8,
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
        }}
        justify="center"
      >
        <Spin tip="Đang tải tin nhắn..." />
      </Row>
    );
  }

  const allMessages = [...optimisticMessages, ...messageIds];

  return (
    <div
      className="message-list-container"
      id="scrollable-message-list"
      ref={messageListRef}
      onMouseEnter={handleMouseEnter}
      onScroll={handleScroll}
      tabIndex={-1}
    >
      {isSending && <LoadingOutlined />}
      {showScrollDown && (
        <div className="scroll-down-wrapper">
          {newMessagePreview && (
            <div className="new-message-preview" onClick={scrollToBottom}>
              <span className="preview-text">
                {newMessagePreview.senderName}: {newMessagePreview.content}
              </span>
            </div>
          )}
          <Button className="scroll-down-button" icon={<ArrowDownOutlined />} onClick={scrollToBottom} />
        </div>
      )}
      <InfiniteScroll
        scrollThreshold="100px"
        dataLength={allMessages.length}
        next={getMessageList}
        hasMore={hasMore}
        loader={
          <div>
            <MessageSkeleton isOwn={false} />
            <MessageSkeleton isOwn={true} />
            <MessageSkeleton isOwn={false} />
          </div>
        }
        style={{ display: 'flex', flexDirection: 'column-reverse' }}
        inverse
        scrollableTarget="scrollable-message-list"
      >
        {allMessages.map((id) => {
          const message = optimisticMessages.find((m) => m.id === id) || messageEntities[id];
          const senderId = _get(message, 'sender._id') || _get(message, 'sender');
          return (
            <MessageCell
              isOwnCell={
                senderId === currentUserId ||
                (['admin', 'super_admin'].includes(currentUserRole) && _get(message, 'isAdmin'))
              }
              key={id}
              data={message}
              onRetry={handleRetry}
            />
          );
        })}
      </InfiniteScroll>
    </div>
  );
};

MessageList.propTypes = {
  messageIds: PropTypes.array.isRequired,
  messageEntities: PropTypes.object.isRequired,
  getMessageList: PropTypes.func,
  hasMore: PropTypes.bool,
  currentUserId: PropTypes.string,
  currentUserRole: PropTypes.string,
  onSendMessage: PropTypes.func.isRequired,
};

export default memo(MessageList);
